<template>
  <div class="ocr-recognize">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span style="font-size: 18px; font-weight: bold;">{{ pageTitle }}</span>
        <span style="margin-left: 20px; color: #909399; font-size: 14px;">
          支持统一OCR识别，适配身份证、营业执照、护照、驾驶证等
        </span>
      </div>

      <!-- API类型选择 -->
      <el-form :inline="true" class="form-section">
        <el-form-item label="API类型">
          <el-select v-model="apiType" placeholder="请选择API类型" style="width: 260px;" @change="handleApiTypeChange">
            <el-option label="智能轮询（推荐）" value="AUTO">
              <span style="float: left">智能轮询</span>
              <span style="float: right; color: #409EFF; font-size: 12px;">★ 推荐</span>
            </el-option>
            <el-option label="全文识别高精版" value="ADVANCED">
              <span style="float: left">全文识别高精版</span>
              <span style="float: right; color: #E6A23C; font-size: 12px;">200次/月</span>
            </el-option>
            <el-option label="通用手写体识别" value="HANDWRITING">
              <span style="float: left">通用手写体识别</span>
              <span style="float: right; color: #67C23A; font-size: 12px;">200次/月</span>
            </el-option>
            <el-option label="通用文字识别" value="GENERAL">
              <span style="float: left">通用文字识别</span>
              <span style="float: right; color: #409EFF; font-size: 12px;">200次/月</span>
            </el-option>
            <el-option label="通用多语言识别" value="MULTILANGUAGE">
              <span style="float: left">通用多语言识别</span>
              <span style="float: right; color: #9C27B0; font-size: 12px;">200次/月</span>
            </el-option>
            <el-option label="英语专项识别" value="ENGLISH">
              <span style="float: left">英语专项识别</span>
              <span style="float: right; color: #F56C6C; font-size: 12px;">200次/月</span>
            </el-option>
          </el-select>
          <el-tooltip placement="top">
            <div slot="content">
              • 智能轮询：自动选择免费额度未用完的API（1000次/月）<br/>
              • 全文识别高精版：高精度识别，适合复杂文档<br/>
              • 通用手写体识别：适合手写文字识别<br/>
              • 通用文字识别：适合一般文字识别<br/>
              • 通用多语言识别：支持100+语言<br/>
              • 英语专项识别：适合英文文档识别
            </div>
            <i class="el-icon-question" style="margin-left: 5px; color: #909399; cursor: pointer;"></i>
          </el-tooltip>
        </el-form-item>
      </el-form>

      <!-- 图片上传区域 -->
      <div class="upload-section" v-if="!imageUrl">
        <el-upload
          class="upload-demo"
          drag
          action="#"
          :auto-upload="false"
          :on-change="handleFileChange"
          :show-file-list="false"
          accept="image/*">
          <div class="upload-placeholder">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将图片拖到此处，或<em>点击上传</em></div>
            <div class="el-upload__tip">支持 JPG、PNG、BMP、PDF 等格式，图片不超过 10MB</div>
          </div>
        </el-upload>
      </div>

      <!-- 图片预览和识别结果区域 -->
      <el-row :gutter="20" v-if="imageUrl">
        <!-- 左侧：图片预览 -->
        <el-col :span="10">
          <div class="image-preview-wrapper">
            <div class="image-preview">
              <div class="magnifier-wrapper" 
                @mousemove="handleMouseMove"
                @mouseenter="showMagnifier = true"
                @mouseleave="showMagnifier = false">
                <el-image
                  ref="previewImage"
                  :src="imageUrl"
                  :style="{ transform: `rotate(${imageRotation}deg)`, cursor: magnifierEnabled ? 'crosshair' : 'pointer' }"
                  fit="contain"
                  :preview-src-list="[imageUrl]">
                  <div slot="error" class="image-error">
                    <i class="el-icon-picture-outline"></i>
                    <p>图片加载失败</p>
                  </div>
                </el-image>
                
                <!-- 放大镜 -->
                <div 
                  v-show="showMagnifier && magnifierEnabled"
                  class="magnifier-glass"
                  :style="magnifierStyle">
                  <img 
                    :src="imageUrl"
                    :style="magnifierImageStyle"
                    alt="放大视图">
                </div>
              </div>
            </div>
            <div class="button-group">
              <el-button-group>
                <el-button size="small" icon="el-icon-refresh-left" @click="rotateImage(-90)">
                  左旋转
                </el-button>
                <el-button size="small" icon="el-icon-refresh-right" @click="rotateImage(90)">
                  右旋转
                </el-button>
                <el-button size="small" icon="el-icon-refresh" @click="resetRotation">
                  重置
                </el-button>
                <el-button 
                  size="small" 
                  :icon="magnifierEnabled ? 'el-icon-zoom-out' : 'el-icon-zoom-in'" 
                  :type="magnifierEnabled ? 'primary' : ''"
                  @click="toggleMagnifier">
                  {{ magnifierEnabled ? '关闭放大镜' : '开启放大镜' }}
                </el-button>
              </el-button-group>
              
              <!-- 放大镜设置 -->
              <div v-if="magnifierEnabled" style="margin-top: 10px; padding: 10px; background-color: #f5f7fa; border-radius: 4px;">
                <div style="margin-bottom: 8px;">
                  <span style="font-size: 12px; color: #606266; margin-right: 10px;">镜片大小：{{ magnifierSize }}px</span>
                  <el-button-group size="mini">
                    <el-button @click="adjustMagnifierSize(-20)" :disabled="magnifierSize <= 100">
                      <i class="el-icon-minus"></i>
                    </el-button>
                    <el-button @click="adjustMagnifierSize(20)" :disabled="magnifierSize >= 300">
                      <i class="el-icon-plus"></i>
                    </el-button>
                  </el-button-group>
                </div>
                <div>
                  <span style="font-size: 12px; color: #606266; margin-right: 10px;">放大倍数：{{ magnifierZoom }}x</span>
                  <el-button-group size="mini">
                    <el-button @click="adjustMagnifierZoom(-0.5)" :disabled="magnifierZoom <= 1.5">
                      <i class="el-icon-minus"></i>
                    </el-button>
                    <el-button @click="adjustMagnifierZoom(0.5)" :disabled="magnifierZoom >= 5">
                      <i class="el-icon-plus"></i>
                    </el-button>
                  </el-button-group>
                </div>
              </div>
              
              <el-button type="primary" @click="recognizeImage" :loading="recognizing" style="margin-top: 10px; width: 100%;">
                <i class="el-icon-search"></i> 开始识别
              </el-button>
              <el-button @click="clearImage" style="margin-top: 10px; width: 100%;">
                <i class="el-icon-delete"></i> 清除图片
              </el-button>
            </div>
          </div>
        </el-col>
        
        <!-- 右侧：识别结果 -->
        <el-col :span="14">
          <div class="result-section" v-if="ocrResult">
            <el-divider content-position="left">识别结果</el-divider>
            
            <!-- 结构化数据 -->
            <div v-if="ocrResult.structuredData && Object.keys(ocrResult.structuredData).length > 0" class="structured-data">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <h3>证件信息</h3>
                <el-button type="success" size="small" @click="showSaveDialog">
                  <i class="el-icon-download"></i> 保存
                </el-button>
              </div>
              <el-descriptions :column="1" border size="small">
                <el-descriptions-item 
                  v-for="item in sortedStructuredData" 
                  :key="item.key" 
                  :label="item.key">
                  {{ item.value }}
                </el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- 文本内容 -->
            <div class="text-content" v-if="ocrResult.fullText">
              <h3>文本内容</h3>
              <el-input
                type="textarea"
                :rows="15"
                v-model="ocrResult.fullText"
                placeholder="识别结果将显示在这里">
              </el-input>
              
              <div class="copy-button">
                <el-button size="small" @click="copyText" icon="el-icon-document-copy">
                  复制文本
                </el-button>
              </div>
            </div>

            <!-- 原始数据 -->
            <el-collapse v-if="ocrResult.rawResponse" style="margin-top: 20px;">
              <el-collapse-item title="查看原始响应数据" name="1">
                <pre class="raw-response">{{ formatJson(ocrResult.rawResponse) }}</pre>
              </el-collapse-item>
            </el-collapse>
          </div>
          <div v-else class="empty-result">
            <el-empty description="点击开始识别后，识别结果将显示在这里"></el-empty>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- 保存证件信息弹窗 -->
    <el-dialog title="保存证件信息" :visible.sync="saveDialogVisible" width="600px">
      <el-form :model="documentForm" label-width="100px" ref="documentForm">
        <el-form-item label="序号" prop="serialNumber">
          <el-input-number v-model="documentForm.serialNumber" :min="1" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="税号" prop="taxNumber">
          <el-input v-model="documentForm.taxNumber"></el-input>
        </el-form-item>
        <el-form-item label="姓氏" prop="surname">
          <el-input v-model="documentForm.surname"></el-input>
        </el-form-item>
        <el-form-item label="名字" prop="givenName">
          <el-input v-model="documentForm.givenName"></el-input>
        </el-form-item>
        <el-form-item label="中间名" prop="middleName">
          <el-input v-model="documentForm.middleName"></el-input>
        </el-form-item>
        <el-form-item label="完整姓名" prop="fullName">
          <el-input v-model="documentForm.fullName"></el-input>
        </el-form-item>
        <el-form-item label="出生日期" prop="dateOfBirth">
          <el-input v-model="documentForm.dateOfBirth"></el-input>
        </el-form-item>
        <el-form-item label="地址" prop="address">
          <el-input type="textarea" :rows="3" v-model="documentForm.address"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="saveDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveDocument" :loading="saving">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 批量识别弹窗 -->
    <el-dialog title="批量识别证件" :visible.sync="batchDialogVisible" width="700px" :close-on-click-modal="false">
      <el-form :model="batchForm" label-width="120px">
        <el-form-item label="文件夹路径">
          <el-input 
            v-model="batchForm.folderPath" 
            placeholder="例如: D:\\菲律宾证书注册\\20251123_2(50份)\\证书文件\\源文件"
            style="width: 100%"
            readonly>
            <template slot="append">
              <el-button icon="el-icon-folder" @click="triggerFolderSelect">选择文件夹</el-button>
            </template>
          </el-input>
          <input 
            ref="folderInput" 
            type="file" 
            webkitdirectory 
            directory 
            multiple 
            style="display: none;" 
            @change="handleFolderSelect" />
          <div style="margin-top: 5px; color: #909399; font-size: 12px;">
            点击“选择文件夹”按钮，选择包含按序号命名的子文件夹的目录
          </div>
        </el-form-item>
        <el-form-item label="图片关键词">
          <el-input v-model="batchForm.imageKeyword" placeholder="默认: FRONT_ID"></el-input>
          <div style="margin-top: 5px; color: #909399; font-size: 12px;">
            只识别文件名包含此关键词的图片
          </div>
        </el-form-item>
      </el-form>
      
      <div v-if="batchProcessing" style="margin-top: 20px;">
        <el-progress :percentage="batchProgress" :status="batchStatus"></el-progress>
        <div style="margin-top: 10px; color: #606266; font-weight: bold;">
          {{ batchMessage }}
        </div>
        <el-divider></el-divider>
        <div style="max-height: 400px; overflow-y: auto; background-color: #f5f7fa; padding: 15px; border-radius: 4px;">
          <el-timeline>
            <el-timeline-item 
              v-for="(log, index) in batchLogs" 
              :key="index"
              :timestamp="log.time"
              :type="log.type"
              :icon="getLogIcon(log)"
              placement="top">
              <span :style="getLogStyle(log)">{{ log.message }}</span>
            </el-timeline-item>
          </el-timeline>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="batchDialogVisible = false" :disabled="batchProcessing">关 闭</el-button>
        <el-button type="primary" @click="startBatchRecognize" :loading="batchProcessing" :disabled="!batchForm.folderPath">
          开始识别
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'OcrRecognize',
  data() {
    return {
      pageTitle: '统一OCR识别', // 默认标题
      apiType: 'AUTO',  // API类型：智能轮询/通用文字/高精版/证件识别
      documentType: 'IdCard',  // 证件类型（仅当apiType为DOCUMENT时使用）
      recognizeType: 'AUTO',  // 保留兼容性
      imageFile: null,
      imageUrl: '',
      recognizing: false,
      ocrResult: null,
      // 图片旋转和放大镜
      imageRotation: 0,
      magnifierEnabled: false,
      showMagnifier: false,
      magnifierX: 0,
      magnifierY: 0,
      magnifierSize: 150,
      magnifierZoom: 2.5,
      imageRect: null,
      // 定义字段显示顺序
      fieldOrder: ['税号', 'SURNAME', 'GIVEN NAME', 'MIDDLE NAME', '姓名', '出生日期', '地址'],
      // 保存弹窗
      saveDialogVisible: false,
      saving: false,
      documentForm: {
        serialNumber: null,
        taxNumber: '',
        surname: '',
        givenName: '',
        middleName: '',
        fullName: '',
        dateOfBirth: '',
        address: '',
        imageUrl: '',
        rawText: ''
      },
      // 批量识别
      batchDialogVisible: false,
      batchProcessing: false,
      batchProgress: 0,
      batchStatus: null,
      batchMessage: '',
      batchLogs: [],
      batchForm: {
        folderPath: '',
        imageKeyword: 'FRONT_ID'
      },
      selectedFiles: [] // 存储选中的文件
    }
  },
  computed: {
    // 放大镜样式
    magnifierStyle() {
      return {
        left: `${this.magnifierX - this.magnifierSize / 2}px`,
        top: `${this.magnifierY - this.magnifierSize / 2}px`,
        width: `${this.magnifierSize}px`,
        height: `${this.magnifierSize}px`
      }
    },
    // 放大镜内图片样式
    magnifierImageStyle() {
      if (!this.imageRect) return {}
      
      // 鼠标在图片容器上的相对位置
      const relativeX = this.magnifierX - this.imageRect.left
      const relativeY = this.magnifierY - this.imageRect.top
      
      // 计算放大后图片的偏移，使鼠标位置的内容显示在放大镜中心
      const scaledX = relativeX * this.magnifierZoom
      const scaledY = relativeY * this.magnifierZoom
      
      // 放大镜中心位置
      const centerX = this.magnifierSize / 2
      const centerY = this.magnifierSize / 2
      
      // 图片左上角的位置 = 中心位置 - 放大后鼠标的位置
      const left = centerX - scaledX
      const top = centerY - scaledY
      
      return {
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
        width: `${this.imageRect.width}px`,
        height: `${this.imageRect.height}px`,
        // 只缩放，不旋转，保证定位准确
        transform: `scale(${this.magnifierZoom})`,
        transformOrigin: '0 0'
      }
    },
    // 按指定顺序排列结构化数据
    sortedStructuredData() {
      if (!this.ocrResult || !this.ocrResult.structuredData) {
        return []
      }
      
      const data = this.ocrResult.structuredData
      const sorted = []
      
      // 按顺序添加字段
      this.fieldOrder.forEach(key => {
        if (data.hasOwnProperty(key)) {
          sorted.push({
            key: key,
            value: data[key]
          })
        }
      })
      
      // 添加其他不在顺序列表中的字段
      Object.keys(data).forEach(key => {
        if (!this.fieldOrder.includes(key)) {
          sorted.push({
            key: key,
            value: data[key]
          })
        }
      })
      
      return sorted
    }
  },
  mounted() {
    this.loadPageTitle()
  },
  methods: {
    // 动态加载页面标题
    async loadPageTitle() {
      try {
        const menuData = localStorage.getItem('admin_menu_data')
        if (menuData) {
          const menus = JSON.parse(menuData)
          const allMenus = menus.menus || []
          const currentMenu = allMenus.find(m => m.url === this.$route.path)
          if (currentMenu) {
            this.pageTitle = currentMenu.name
          }
        }
      } catch (error) {
        console.error('加载页面标题失败:', error)
      }
    },
    
    // 处理API类型变更
    handleApiTypeChange(value) {
      // 同步更新recognizeType保持兼容
      this.recognizeType = value
      
      // 如果不是证件识别类型，清空证件类型
      if (value !== 'DOCUMENT') {
        this.documentType = 'IdCard'
      }
    },
    handleFileChange(file) {
      this.imageFile = file.raw
      this.imageUrl = URL.createObjectURL(file.raw)
      this.ocrResult = null
      this.imageRotation = 0 // 重置旋转角度
      this.magnifierEnabled = false // 关闭放大镜
    },
    
    clearImage() {
      this.imageFile = null
      this.imageUrl = ''
      this.ocrResult = null
      this.imageRotation = 0
      this.magnifierEnabled = false
    },
    
    // 旋转图片
    rotateImage(degree) {
      this.imageRotation = (this.imageRotation + degree) % 360
      // 延迟一下，让用户先看到CSS旋转效果
      setTimeout(() => {
        this.applyRotation()
      }, 350)
    },
    
    // 应用旋转，生成新的图片
    async applyRotation() {
      if (!this.imageFile || this.imageRotation === 0) return
      
      try {
        // 创建一个Image对象加载原始图片
        const img = new Image()
        img.src = URL.createObjectURL(this.imageFile)
        
        await new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = reject
        })
        
        // 创建canvas
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        // 根据旋转角度设置canvas尺寸
        const angle = this.imageRotation
        if (angle === 90 || angle === 270 || angle === -90 || angle === -270) {
          // 90度和270度旋转需要交换宽高
          canvas.width = img.height
          canvas.height = img.width
        } else {
          canvas.width = img.width
          canvas.height = img.height
        }
        
        // 移动到中心点
        ctx.translate(canvas.width / 2, canvas.height / 2)
        
        // 旋转
        ctx.rotate(angle * Math.PI / 180)
        
        // 绘制图片（以中心为原点）
        ctx.drawImage(img, -img.width / 2, -img.height / 2)
        
        // 将canvas转换为blob
        canvas.toBlob((blob) => {
          if (blob) {
            // 创建新的File对象
            const rotatedFile = new File([blob], this.imageFile.name, {
              type: this.imageFile.type,
              lastModified: Date.now()
            })
            
            // 替换图片
            this.imageFile = rotatedFile
            this.imageUrl = URL.createObjectURL(rotatedFile)
            
            // 重置旋转角度（因为已经应用到图片上了）
            this.imageRotation = 0
          }
        }, this.imageFile.type || 'image/jpeg')
        
      } catch (error) {
        console.error('旋转图片失败', error)
        this.$message.error('旋转图片失败')
      }
    },
    
    // 重置旋转
    resetRotation() {
      // 如果当前有临时旋转角度，直接清零
      if (this.imageRotation !== 0) {
        this.imageRotation = 0
      } else if (this.imageFile) {
        // 如果没有临时角度但图片已被旋转，重新上传原始图片
        // 这里只能提示用户重新上传
        this.$message.info('请重新上传图片恢复原始方向')
      }
    },
    
    // 切换放大镜
    toggleMagnifier() {
      this.magnifierEnabled = !this.magnifierEnabled
      if (!this.magnifierEnabled) {
        this.showMagnifier = false
      }
    },
    
    // 调整放大镜大小
    adjustMagnifierSize(delta) {
      const newSize = this.magnifierSize + delta
      if (newSize >= 100 && newSize <= 300) {
        this.magnifierSize = newSize
      }
    },
    
    // 调整放大倍数
    adjustMagnifierZoom(delta) {
      const newZoom = this.magnifierZoom + delta
      if (newZoom >= 1.5 && newZoom <= 5) {
        this.magnifierZoom = newZoom
      }
    },
    
    // 处理鼠标移动
    handleMouseMove(event) {
      if (!this.magnifierEnabled) return
      
      const imageElement = this.$refs.previewImage?.$el?.querySelector('img')
      if (!imageElement) return
      
      // 获取图片预览容器
      const previewContainer = event.currentTarget.closest('.image-preview')
      if (!previewContainer) return
      
      const imageRect = imageElement.getBoundingClientRect()
      const wrapperRect = event.currentTarget.getBoundingClientRect()
      const containerRect = previewContainer.getBoundingClientRect()
      
      // 计算鼠标相对于包装器的位置
      const mouseX = event.clientX - wrapperRect.left
      const mouseY = event.clientY - wrapperRect.top
      
      // 计算图片相对于包装器的位置
      const imageOffsetX = imageRect.left - wrapperRect.left
      const imageOffsetY = imageRect.top - wrapperRect.top
      
      // 保存图片信息供计算属性使用
      this.imageRect = {
        left: imageOffsetX,
        top: imageOffsetY,
        width: imageRect.width,
        height: imageRect.height
      }
      
      // 设置放大镜位置
      this.magnifierX = mouseX
      this.magnifierY = mouseY
    },
    
    async recognizeImage() {
      if (!this.imageFile) {
        this.$message.warning('请先上传图片')
        return
      }

      this.recognizing = true
      
      try {
        const formData = new FormData()
        formData.append('file', this.imageFile)
        
        // 根据API类型决定传递的type参数
        // DOCUMENT类型：直接传递"DOCUMENT"，后端默认使用IdCard
        // 其他类型：传递API类型本身（AUTO/GENERAL/ADVANCED）
        let typeParam = this.apiType
        
        formData.append('type', typeParam)

        const token = localStorage.getItem('admin_token')
        const response = await axios.post(
          '/admin/ocr/recognize/document',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.code === 200) {
          const result = response.data.data
          // 检查识别是否成功
          if (result && result.success) {
            this.ocrResult = result
            this.$message.success('识别成功')
          } else {
            // 识别失败，显示具体错误信息
            const errorMsg = result?.errorMessage || response.data.message || response.data.msg || '识别失败'
            this.$message.error(errorMsg)
          }
        } else {
          // HTTP错误，直接显示response.data.message
          this.$message.error(response.data.message || response.data.msg || '识别失败')
        }
      } catch (error) {
        console.error('识别失败', error)
        this.$message.error('识别失败：' + (error.response?.data?.message || error.response?.data?.msg || error.message))
      } finally {
        this.recognizing = false
      }
    },
    
    copyText() {
      if (!this.ocrResult || !this.ocrResult.fullText) {
        return
      }
      
      const textarea = document.createElement('textarea')
      textarea.value = this.ocrResult.fullText
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      
      this.$message.success('已复制到剪贴板')
    },
    
    formatJson(jsonStr) {
      try {
        const obj = JSON.parse(jsonStr)
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return jsonStr
      }
    },
    
    // 显示保存对话框
    async showSaveDialog() {
      if (!this.ocrResult || !this.ocrResult.structuredData) {
        this.$message.warning('没有可保存的证件信息')
        return
      }
      
      const data = this.ocrResult.structuredData
      
      // 获取当前最大序号
      let nextSerialNumber = 1
      try {
        const token = localStorage.getItem('admin_token')
        const response = await axios.get('/admin/ocr/document/next-serial', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.code === 200) {
          nextSerialNumber = response.data.data
        }
      } catch (error) {
        console.error('获取序号失败', error)
      }
      
      // 将图片转换为base64
      let imageBase64 = ''
      if (this.imageFile) {
        try {
          imageBase64 = await this.fileToBase64(this.imageFile)
        } catch (error) {
          console.error('图片转换失败', error)
        }
      }
      
      // 填充表单数据
      this.documentForm = {
        serialNumber: nextSerialNumber,
        taxNumber: data['税号'] || '',
        surname: data['SURNAME'] || '',
        givenName: data['GIVEN NAME'] || '',
        middleName: data['MIDDLE NAME'] || '',
        fullName: data['姓名'] || '',
        dateOfBirth: data['出生日期'] || '',
        address: data['地址'] || '',
        imageUrl: imageBase64,
        rawText: this.ocrResult.fullText || ''
      }
      
      this.saveDialogVisible = true
    },
    
    // 保存证件信息
    async saveDocument() {
      this.saving = true
      
      try {
        const token = localStorage.getItem('admin_token')
        const response = await axios.post(
          '/admin/ocr/document/save',
          this.documentForm,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )
        
        if (response.data.code === 200) {
          this.$message.success('保存成功')
          this.saveDialogVisible = false
        } else {
          this.$message.error(response.data.msg || '保存失败')
        }
      } catch (error) {
        console.error('保存失败', error)
        this.$message.error('保存失败：' + (error.response?.data?.msg || error.message))
      } finally {
        this.saving = false
      }
    },
    
    // 显示批量识别对话框
    showBatchDialog() {
      this.batchDialogVisible = true
      this.batchProgress = 0
      this.batchStatus = null
      this.batchMessage = ''
      this.batchLogs = []
      this.selectedFiles = []
      this.batchForm.folderPath = ''
    },
    
    // 触发文件夹选择
    triggerFolderSelect() {
      this.$refs.folderInput.click()
    },
    
    // 处理文件夹选择
    handleFolderSelect(event) {
      const files = event.target.files
      if (!files || files.length === 0) {
        return
      }
      
      this.selectedFiles = Array.from(files)
      
      // 获取文件夹路径（从第一个文件的路径提取）
      if (files[0].webkitRelativePath) {
        const fullPath = files[0].webkitRelativePath
        const pathParts = fullPath.split('/')
        
        // 尝试从完整路径中提取更多层级信息
        // 构建显示路径：显示根文件夹名称，并统计子文件夹数量
        const rootFolder = pathParts[0]
        
        // 统计唯一的子文件夹（第二层）
        const subFolders = new Set()
        Array.from(files).forEach(file => {
          if (file.webkitRelativePath) {
            const parts = file.webkitRelativePath.split('/')
            if (parts.length >= 2) {
              subFolders.add(parts[1])
            }
          }
        })
        
        // 显示格式：根文件夹名称 (包含X个子文件夹，共Y个文件)
        const displayPath = `${rootFolder} (包含${subFolders.size}个子文件夹，共${files.length}个文件)`
        this.batchForm.folderPath = displayPath
        // 删除选择文件夹后的提示
      }
    },
    
    // 选择文件夹（旧方法，保留以防兼容）
    selectFolder() {
      // 删除提示，用户可从输入框中看到文件夹信息
    },
    
    // 添加日志
    addBatchLog(message, type = 'primary') {
      const now = new Date()
      const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`
      this.batchLogs.push({
        time,
        message,
        type
      })
      
      // 自动滚动到最后
      this.$nextTick(() => {
        const logContainer = document.querySelector('.el-timeline')
        if (logContainer) {
          logContainer.scrollTop = logContainer.scrollHeight
        }
      })
    },
    
    // 获取日志图标
    getLogIcon(log) {
      if (log.message.includes('✅') || log.message.includes('成功')) {
        return 'el-icon-success'
      } else if (log.message.includes('❌') || log.message.includes('失败')) {
        return 'el-icon-error'
      } else if (log.message.includes('✨') || log.message.includes('开始')) {
        return 'el-icon-star-on'
      } else if (log.message.includes('📁')) {
        return 'el-icon-folder'
      } else if (log.message.includes('🔍')) {
        return 'el-icon-search'
      } else if (log.message.includes('📊') || log.message.includes('📝')) {
        return 'el-icon-document'
      }
      return 'el-icon-info'
    },
    
    // 获取日志样式
    getLogStyle(log) {
      if (log.message.includes('✅') || log.type === 'success') {
        return 'color: #67C23A; font-weight: 500;'
      } else if (log.message.includes('❌') || log.type === 'danger') {
        return 'color: #F56C6C; font-weight: 500;'
      } else if (log.message.includes('✨')) {
        return 'color: #409EFF; font-weight: bold; font-size: 15px;'
      } else if (log.message.startsWith('  [')) {
        return 'font-family: monospace; font-size: 13px;'
      }
      return 'color: #606266;'
    },
    
    // 开始批量识别
    async startBatchRecognize() {
      console.log('=== 开始批量识别 ===')
      if (!this.batchForm.folderPath && this.selectedFiles.length === 0) {
        this.$message.warning('请选择文件夹')
        return
      }
      
      console.log('已选择文件:', this.selectedFiles.length)
      
      this.batchProcessing = true
      this.batchProgress = 0
      this.batchStatus = null
      this.batchLogs = []
      this.batchMessage = '正在处理文件...'
      
      this.addBatchLog('✨ 开始批量识别任务')
      this.addBatchLog('📁 文件夹: ' + this.batchForm.folderPath)
      this.addBatchLog('🔍 图片关键词: ' + (this.batchForm.imageKeyword || 'FRONT_ID'))
      this.addBatchLog('🎯 识别类型: ' + this.recognizeType)
      
      try {
        // 按文件夹组织文件
        const folderMap = this.organizeFilesByFolder()
        console.log('folderMap:', folderMap)
        const folderNames = Object.keys(folderMap).sort((a, b) => {
          const numA = this.extractNumber(a)
          const numB = this.extractNumber(b)
          if (numA !== null && numB !== null) {
            return numA - numB
          }
          return a.localeCompare(b)
        })
        
        this.addBatchLog(`📊 共找到 ${folderNames.length} 个子文件夹`)
        this.addBatchLog('')
        
        console.log('找到子文件夹数量:', folderNames.length)
        
        const token = localStorage.getItem('admin_token')
        let total = 0
        let success = 0
        let failed = 0
        const details = []
        
        // 逐个处理文件夹
        for (let i = 0; i < folderNames.length; i++) {
          const folderName = folderNames[i]
          const files = folderMap[folderName]
          total++
          
          console.log(`\n=== 处理第 ${i + 1} 个文件夹: ${folderName} ===`)
          
          // 提取序号
          const serialNumber = this.extractNumber(folderName) || total
          
          this.addBatchLog(`[${i + 1}/${folderNames.length}] 正在处理: ${folderName} (序号: ${serialNumber})`)
          
          try {
            // 查找包含关键词的图片
            const targetFile = files.find(file => 
              file.name.includes(this.batchForm.imageKeyword || 'FRONT_ID')
            )
            
            console.log('查找目标文件, 关键词:', this.batchForm.imageKeyword || 'FRONT_ID')
            console.log('找到的文件:', targetFile?.name)
            
            if (!targetFile) {
              this.addBatchLog(`  ❌ 未找到包含 '${this.batchForm.imageKeyword}' 的图片`, 'danger')
              failed++
              details.push({
                serialNumber,
                success: false,
                error: `未找到包含 '${this.batchForm.imageKeyword}' 的图片`
              })
              continue
            }
            
            this.addBatchLog(`  🖼️ 找到图片: ${targetFile.name}`)
            
            console.log('准备发送OCR请求...')
            
            // 识别图片（不传递type，使用OCR配置的API类型）
            const formData = new FormData()
            formData.append('file', targetFile)
            // formData.append('type', this.recognizeType)  // 不传递type，让后端根据配置决定
            
            this.addBatchLog(`  🔍 开始OCR识别...`)
            
            let ocrResult = null
            try {
              const ocrResponse = await axios.post(
                '/admin/ocr/recognize/document',
                formData,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                  }
                }
              )
              
              if (ocrResponse.data.code !== 200 || !ocrResponse.data.data.success) {
                const errorMsg = ocrResponse.data.data?.errorMessage || ocrResponse.data.message || ocrResponse.data.msg || 'OCR识别失败'
                this.addBatchLog(`  ❌ OCR识别失败: ${errorMsg}`, 'danger')
                failed++
                details.push({
                  serialNumber,
                  success: false,
                  error: `OCR识别失败: ${errorMsg}`
                })
                continue
              }
              
              ocrResult = ocrResponse.data.data
              this.addBatchLog(`  ✅ OCR识别成功`)
            } catch (ocrError) {
              // 捕获axios请求异常（如500错误）
              console.error('OCR请求异常:', ocrError)
              console.log('ocrError.response:', ocrError.response)
              console.log('ocrError.response?.data:', ocrError.response?.data)
              
              const errorMsg = ocrError.response?.data?.message || ocrError.response?.data?.msg || ocrError.message || 'OCR识别失败'
              console.log('提取的错误信息:', errorMsg)
              
              this.addBatchLog(`  ❌ OCR识别失败: ${errorMsg}`, 'danger')
              failed++
              details.push({
                serialNumber,
                success: false,
                error: `OCR识别失败: ${errorMsg}`
              })
              continue
            }
            
            // 如果识别失败，不继续后续处理
            if (!ocrResult) {
              continue
            }
            
            // 将图片转换为base64
            const imageBase64 = await this.fileToBase64(targetFile)
            
            // 准备保存数据
            const documentData = {
              serialNumber: serialNumber,
              taxNumber: '',
              surname: '',
              givenName: '',
              middleName: '',
              fullName: '',
              dateOfBirth: '',
              address: '',
              imageUrl: imageBase64,
              rawText: ocrResult.fullText || ''
            }
            
            if (ocrResult.structuredData) {
              documentData.taxNumber = ocrResult.structuredData['税号'] || ''
              documentData.surname = ocrResult.structuredData['SURNAME'] || ''
              documentData.givenName = ocrResult.structuredData['GIVEN NAME'] || ''
              documentData.middleName = ocrResult.structuredData['MIDDLE NAME'] || ''
              documentData.fullName = ocrResult.structuredData['姓名'] || ''
              documentData.dateOfBirth = ocrResult.structuredData['出生日期'] || ''
              documentData.address = ocrResult.structuredData['地址'] || ''
            }
            
            // 保存到数据库
            this.addBatchLog(`  💾 正在保存...`)
            const saveResponse = await axios.post(
              '/admin/ocr/document/save',
              documentData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            )
            
            if (saveResponse.data.code === 200) {
              this.addBatchLog(`  ✅ 保存成功: ${documentData.fullName}`, 'success')
              success++
              details.push({
                serialNumber,
                success: true,
                fullName: documentData.fullName
              })
            } else {
              this.addBatchLog(`  ❌ 保存失败`, 'danger')
              failed++
              details.push({
                serialNumber,
                success: false,
                error: '保存失败'
              })
            }
            
          } catch (error) {
            console.error(`处理 ${folderName} 失败`, error)
            this.addBatchLog(`  ❌ 处理失败: ${error.message}`, 'danger')
            failed++
            details.push({
              serialNumber,
              success: false,
              error: error.message
            })
          }
          
          // 更新进度
          this.batchProgress = Math.round(((i + 1) / folderNames.length) * 100)
          this.addBatchLog('')
        }
        
        // 显示统计结果
        this.batchProgress = 100
        this.batchStatus = failed > 0 ? 'exception' : 'success'
        const successRate = total > 0 ? ((success / total) * 100).toFixed(2) : '0.00'
        this.batchMessage = `批量识别完成！总计: ${total}, 成功: ${success}, 失败: ${failed}, 成功率: ${successRate}%`
        
        this.addBatchLog('✅ 批量识别完成！', failed > 0 ? 'warning' : 'success')
        this.addBatchLog(`📊 总计: ${total} 个文件夹`, 'success')
        this.addBatchLog(`✅ 成功: ${success} 个`, 'success')
        this.addBatchLog(`❌ 失败: ${failed} 个`, failed > 0 ? 'danger' : 'success')
        this.addBatchLog(`🎯 成功率: ${successRate}%`, 'success')
        
        if (failed > 0) {
          this.addBatchLog('', 'warning')
          this.addBatchLog('⚠️ 有失败记录，请查看上方日志了解详情', 'warning')
        }
        // 不弹出提示，让用户查看页面上的统计信息和日志
        
      } catch (error) {
        console.error('批量识别失败', error)
        this.batchProgress = 0
        this.batchStatus = 'exception'
        this.batchMessage = '批量识别失败：' + error.message
        this.addBatchLog('❌ 批量识别失败：' + error.message, 'danger')
        // 删除弹窗提示，错误信息已显示在页面上
      } finally {
        this.batchProcessing = false
      }
    },
    
    // 按文件夹组织文件
    organizeFilesByFolder() {
      const folderMap = {}
      
      this.selectedFiles.forEach(file => {
        if (file.webkitRelativePath) {
          const pathParts = file.webkitRelativePath.split('/')
          // pathParts[0] 是根文件夹，pathParts[1] 是子文件夹
          if (pathParts.length >= 2) {
            const folderName = pathParts[1]
            if (!folderMap[folderName]) {
              folderMap[folderName] = []
            }
            folderMap[folderName].push(file)
          }
        }
      })
      
      return folderMap
    },
    
    // 从文件夹名称提取数字
    extractNumber(str) {
      const match = str.match(/\d+/)
      return match ? parseInt(match[0]) : null
    },
    
    // 将文件转换为base64
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    }
  }
}
</script>

<style scoped>
.ocr-recognize {
  padding: 20px;
}

.box-card {
  margin-bottom: 20px;
}

.form-section {
  margin-bottom: 20px;
}

.upload-section {
  margin: 30px 0;
}

.upload-demo {
  width: 100%;
}

/* 上传组件的拖拽区域样式 */
.upload-demo >>> .el-upload-dragger {
  width: 100%;
  height: auto;
  min-height: 200px;
  padding: 0;
}

/* 有图片时，取消拖拽区域的高度限制 */
.upload-demo.has-image >>> .el-upload-dragger {
  height: auto;
  border: none;
  background-color: transparent;
}

.upload-placeholder {
  padding: 60px 0;
  text-align: center;
}

.upload-placeholder i {
  font-size: 67px;
  color: #C0C4CC;
  margin-bottom: 16px;
}

.upload-placeholder .el-upload__text {
  color: #606266;
  font-size: 14px;
  margin-top: 10px;
}

.upload-placeholder .el-upload__tip {
  color: #909399;
  font-size: 12px;
  margin-top: 7px;
}

.image-preview-wrapper {
  position: sticky;
  top: 20px;
}

.image-preview {
  padding: 10px;
  text-align: center;
  width: 100%;
  min-height: 200px;
  max-height: 70vh;
  overflow: auto;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 15px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 放大镜容器 */
.magnifier-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  line-height: 0;
}

/* 放大镜镜片 */
.magnifier-glass {
  position: absolute;
  border: 3px solid #409EFF;
  border-radius: 50%;
  cursor: none;
  pointer-events: none;
  z-index: 9999;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.magnifier-glass img {
  position: absolute;
  max-width: none !important;
  max-height: none !important;
  width: auto;
  height: auto;
  pointer-events: none;
  object-fit: none;
  display: block;
}

.image-preview >>> .el-image {
  max-width: 100%;
  max-height: calc(70vh - 40px);
  width: auto !important;
  height: auto !important;
  transition: transform 0.3s ease;
  display: block;
}

.image-preview >>> .el-image img {
  max-width: 100%;
  max-height: calc(70vh - 40px);
  width: auto !important;
  height: auto !important;
  object-fit: contain;
}

.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 450px;
  color: #909399;
}

.image-error i {
  font-size: 64px;
  margin-bottom: 10px;
}

.image-error p {
  font-size: 14px;
  margin: 0;
}

.button-group {
  text-align: center;
  margin-top: 20px;
}

/* 图片预览滚动条样式 */
.image-preview::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.image-preview::-webkit-scrollbar-thumb {
  background-color: #dcdfe6;
  border-radius: 4px;
}

.image-preview::-webkit-scrollbar-thumb:hover {
  background-color: #c0c4cc;
}

.image-preview::-webkit-scrollbar-track {
  background-color: #f5f7fa;
  border-radius: 4px;
}

.result-section {
  padding: 15px;
  background-color: #fff;
  border-radius: 4px;
  min-height: 400px;
}

.empty-result {
  padding: 50px 0;
  text-align: center;
}

.structured-data {
  margin-bottom: 30px;
}

.structured-data h3 {
  margin-bottom: 15px;
  color: #303133;
  font-size: 16px;
}

.text-content h3 {
  margin-bottom: 15px;
  color: #303133;
  font-size: 16px;
}

.copy-button {
  margin-top: 10px;
  text-align: right;
}

.raw-response {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
  max-height: 400px;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
